import { useState } from 'react';

/** Collapsible section, shared by the read-only view and the program editor. */
export function Section({ title, count, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <>
      <button className="ne-sect" onClick={() => setOpen(!open)}>
        <span className="ne-sect-caret">{open ? '▾' : '▸'}</span>
        <span>{title}</span>
        {count != null && <span className="ne-sect-count">{count}</span>}
      </button>
      {open && <div className="ne-sect-body">{children}</div>}
    </>
  );
}

/** Plain-language explanation of the ring-barrier editor. Lives beside the panel, not over it. */
export default function SignalHelp({ onClose, style }) {
  return (
    <div className="ne-help" style={style}>
      <div className="ne-help-head">
        <span>How this works</span>
        <button className="ne-help-close" onClick={onClose} title="Close">×</button>
      </div>

      <h4>The chart is one cycle, left to right</h4>
      <p>
        Each box is a <b>phase</b> — one set of movements that gets green
        together, such as “northbound through and right”.
      </p>

      <h4>Two rows, because two halves of the junction run side by side</h4>
      <p>
        One phase from the top row and one from the bottom can be green at the
        same time, as long as they do not conflict. A box spanning both rows has
        no partner: it runs on its own.
      </p>

      <h4>The orange line is a barrier</h4>
      <p>
        Both rows have to arrive at it together before either moves on. That is
        why lengthening one phase always shortens another — there is a fixed
        amount of time to hand out, and the editor will not let you break it.
      </p>

      <h4>What you can change</h4>
      <p>
        Drag a <b>divider</b> to move green time between two neighbouring
        phases. Drag the <b>barrier</b> to move time between the two halves of
        the cycle. Change <span className="ne-help-key">Cycle</span> to make the
        whole thing longer or shorter.
      </p>

      <h4>Why it says “split” and not “green”</h4>
      <p>
        A split is the phase’s whole slice: its green plus the yellow and
        all-red that follow. Green is what is left after those. Splits are what
        have to add up, so splits are what you set.
      </p>

      <h4>Recall, and why it matters here</h4>
      <p>
        A phase normally runs only when a detector calls it. <b>Recall</b> makes
        it run anyway. This network has no detectors at all, so without recall on
        the coordinated phases the signal gaps out immediately and never reaches
        the splits you set. Max recall on every phase makes it a fixed-time
        signal, where green equals the split exactly.
      </p>

      <h4>What the result compares</h4>
      <p>
        Your program against the one this network came with — a free-running
        actuated signal. So part of any difference is the switch to a fixed,
        coordinated cycle, not only the splits you moved.
      </p>

      <h4>One thing to keep in mind</h4>
      <p>
        These timings were generated from the road network, not surveyed from the
        real controller. Treat any before-and-after as a comparison between two
        assumed states.
      </p>
    </div>
  );
}
